import { useEffect, useState, useRef } from "react";
import { useLocation, useSearchParams } from "wouter";
import { useVerifyPayment } from "@/hooks/use-paystack";
import { useToast } from "@/hooks/use-toast";
import { CircleCheck, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { queryClient } from "@/lib/queryClient";
import { useMobile } from "@/providers/mobile-provider"; 
import { Browser } from '@capacitor/browser'; // Import Browser directly
import { Capacitor } from '@capacitor/core'; // Import Capacitor for platform detection

export default function PaymentCallbackPage() {
  const [_, setLocation] = useLocation();
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState<
    "loading" | "success" | "error"
  >("loading");
  const verifyPayment = useVerifyPayment();
  const { toast } = useToast();
  const { user, loginWithToken } = useAuth();
  const [paymentReference] = useState(searchParams.get("reference"));
  const [authToken] = useState(searchParams.get("token"));
  const verificationCompleted = useRef(false);
  const { isNative } = useMobile();
  const isNativePlatform = Capacitor.isNativePlatform(); // Direct check

  // Handle authentication and verification
  useEffect(() => {
    const initializeAuth = async () => {
      // If we have an auth token and no user, try to authenticate
      if (authToken && !user) {
        try {
          await loginWithToken(authToken);
        } catch (error) {
          console.error("Failed to authenticate with token:", error);
          setLocation("/auth");
          return;
        }
      }

      if (!paymentReference) {
        setLocation("/shop");
        return;
      }

      // Check for cached verification
      const cachedVerification = localStorage.getItem(
        `payment_verified_${paymentReference}`,
      );

      if (cachedVerification) {
        const result = JSON.parse(cachedVerification);
        setVerificationStatus(result.success ? "success" : "error");
        return;
      }

      // Only verify if not already completed
      if (!verificationCompleted.current) {
        verificationCompleted.current = true;
        verifyTransaction(paymentReference);
      }
    };

    initializeAuth();
  }, [authToken, user, paymentReference]);

  const verifyTransaction = async (reference: string) => {
    try {
      // Clean up any previous verification data
      queryClient.removeQueries({
        queryKey: [`/api/currency/paystack/verify?reference=${reference}`],
      });

      const result = await verifyPayment.mutateAsync(reference);

      // Cache the verification result
      localStorage.setItem(
        `payment_verified_${reference}`,
        JSON.stringify({
          success: result.success,
          timestamp: new Date().toISOString(),
        }),
      );

      if (result.success) {
        setVerificationStatus("success");
        toast({
          title: "Payment Successful",
          description: `Your coins have been added to your account.`,
          variant: "default",
        });
      } else {
        setVerificationStatus("error");
        toast({
          title: "Payment Failed",
          description: result.message || "Failed to verify payment",
          variant: "destructive",
        });
      }
    } catch (error) {
      localStorage.setItem(
        `payment_verified_${reference}`,
        JSON.stringify({
          success: false,
          timestamp: new Date().toISOString(),
        }),
      );

      setVerificationStatus("error");
      toast({
        title: "Verification Error",
        description: "There was an error verifying your payment",
        variant: "destructive",
      });
    }
  };
  
  // Close the in-app browser directly with the Browser plugin
  const closeBrowserAndReturn = async () => {
    try {
      if (isNativePlatform) {
        console.log('Closing in-app browser');
        await Browser.close();
        // Give the browser time to close before navigation
        setTimeout(() => {
          setLocation("/shop");
        }, 300);
      } else {
        setLocation("/shop");
      }
    } catch (error) {
      console.error('Error closing browser:', error);
      // Fallback
      setLocation("/shop");
    }
  };

  if (verificationStatus === "loading") {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full p-8 rounded-lg bg-card border border-border shadow-lg">
          <div className="flex flex-col items-center text-center gap-6">
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
            <div>
              <h1 className="text-2xl font-bold">Verifying Payment</h1>
              <p className="text-muted-foreground mt-2">
                Please wait while we verify your payment...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === "success") {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full p-8 rounded-lg bg-card border border-border shadow-lg">
          <div className="flex flex-col items-center text-center gap-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center text-green-500">
              <CircleCheck className="h-10 w-10" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Payment Successful!</h1>
              <p className="text-muted-foreground mt-2">
                Your coins have been added to your account. Thank you for your
                purchase!
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Reference: {paymentReference}
              </p>
              <p className="text-xs text-amber-500 mt-3 border border-amber-500/20 bg-amber-500/10 p-2 rounded">
                All purchases are final and non-refundable as stated in our{" "}
                <a
                  href="/terms-of-service"
                  className="text-primary hover:underline"
                  target="_blank"
                >
                  Terms of Service
                </a>
                .
              </p>
            </div>
            <div className="flex gap-4 mt-4">
              <Button onClick={closeBrowserAndReturn}>
                Return to Shop
              </Button>
              <Button
                variant="outline"
                onClick={() => setLocation("/shop?tab=transactions")}
              >
                View Transactions
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === "error") {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full p-8 rounded-lg bg-card border border-border shadow-lg">
          <div className="flex flex-col items-center text-center gap-6">
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center text-destructive">
              <AlertTriangle className="h-10 w-10" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                Payment Verification Failed
              </h1>
              <p className="text-muted-foreground mt-2">
                We couldn't verify your payment. If you've already been charged,
                please contact support.
              </p>
            </div>
            <div className="flex gap-4 mt-4">
              <Button onClick={closeBrowserAndReturn}>
                Return to Shop
              </Button>
              <Button onClick={() => verifyTransaction(paymentReference || "")}>
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
